import { useState, useEffect } from "react"
import { navigate } from "vike/client/router"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Image from "aldoo-ra/Image"
import MenuGroup from "aldoo-ra/MenuGroup"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { getStoreAccount } from "aldoo-ra/Store/api"
import ReleaseNotes from "./ReleaseNotes"
import {
  RectangleGroupIcon,
  PencilSquareIcon,
  LinkIcon,
} from "@heroicons/react/24/outline"

// Built inside the component (not module-level) since titles/text come
// from the CMS texts bundle.
function getHeroFeatures(texts) {
  return [
    {
      icon: RectangleGroupIcon,
      title: texts?.hero_feature_1_title,
      text: texts?.hero_feature_1_text,
    },
    {
      icon: PencilSquareIcon,
      title: texts?.hero_feature_2_title,
      text: texts?.hero_feature_2_text,
    },
    {
      icon: LinkIcon,
      title: texts?.hero_feature_3_title,
      text: texts?.hero_feature_3_text,
    },
  ]
}

// Any paid entitlement routes the download CTAs to /account/downloads.
// account_type is synced server-side from live entitlements (subscriptions,
// licenses AND one-time purchases) — checking subscriptions alone sent
// lifetime-license users to /pricing while the account Downloads page
// rightly served them.
function useHasActiveSubscription() {
  const [auth] = PlatformAuth.useAuth()
  const [hasSubscription, setHasSubscription] = useState(false)

  useEffect(() => {
    if (!auth?.auth_token) {
      setHasSubscription(false)
      return
    }
    let cancelled = false
    getStoreAccount().then((res) => {
      if (cancelled) return
      const account = res?.result || res
      if (account?.account_type && account.account_type !== "free") {
        setHasSubscription(true)
        return
      }
      const subs = account?.subscriptions || []
      const active = subs.some(
        (s) => s.pid && s.pid !== "transfer-plan-free" && (s.status === "active" || s.status === "trialing")
      )
      setHasSubscription(active)
    })
    return () => {
      cancelled = true
    }
  }, [auth?.auth_token])

  return hasSubscription
}

// CMS defines the Downloads item via options.action="downloads" rather
// than a link, since it has no CMS page (mirrors NavbarDesktop).
const DOWNLOADS_ACTION = "downloads"
const DOWNLOADS_URL = "/account/downloads"

const menuGroupStyle = {
  menu: "flex flex-row items-center py-0 my-0",
  menu_item: "list-none px-0 py-0 my-0",
}

function DownloadCTA({ hasSubscription, variant, className }) {
  if (hasSubscription) {
    return (
      <MenuGroup
        name="DownloadForMacSubscription"
        renderAs="hlist"
        itemsAsLinks={false}
        styleOverrides={menuGroupStyle}
        menuItemRender={(item) => {
          if (item.options?.action !== DOWNLOADS_ACTION) return null
          return (
            <Button variant={variant} onClick={() => navigate(DOWNLOADS_URL)} className={className}>
              {item.label}
            </Button>
          )
        }}
      />
    )
  }

  return (
    <MenuGroup
      name="DownloadForMacNoSubscription"
      renderAs="hlist"
      itemsAsLinks={false}
      styleOverrides={menuGroupStyle}
      menuItemRender={(item) => (
        <Button variant={variant} onClick={() => navigate(item.link?.[0]?.url)} className={className}>
          {item.label}
        </Button>
      )}
    />
  )
}

function HeroMobile() {
  const hasSubscription = useHasActiveSubscription()
  const texts = TextBundle({ id: "home_hero_section" })
  const heroFeatures = getHeroFeatures(texts)

  return (
    <section className="relative lg:hidden px-6 pt-24 pb-8 rounded-b-[30px] sm:rounded-b-[50px] md:rounded-b-[70px] overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-secondary/15 to-secondary/30 pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center justify-center text-center gap-8">
        <div className="flex flex-col items-center gap-4">
          <Typography variant="h1">
            {texts?.hero_title_part1}{" "}
            <span className="text-primary">{texts?.hero_title_part2}</span>
          </Typography>

          <Typography variant="body1" className="mb-0">
            {texts?.hero_subtitle_line1} <br/> {texts?.hero_subtitle_line2}
          </Typography>
        </div>

        <Image id="home_hero" className="w-full max-w-[800px]  h-auto object-contain" />

        <div className="flex flex-col items-center gap-5 w-full">
          <ul className="flex flex-col gap-1 md:gap-4 mt-0">
            {heroFeatures.map((feature) => (
              <li key={feature.title} className="flex items-start gap-2">
                <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary/50 to-accent text-white flex items-center justify-center shrink-0 mt-1">
                  <feature.icon className="w-7 h-7" />
                </span>

                <div>
                  <Typography
                    variant="body1_medium"
                    className="text-left my-0"
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="txt_dim"
                    className="block text-left my-0"
                  >
                    {feature.text}
                  </Typography>
                </div>
              </li>
            ))}
          </ul>

          <div className="w-full max-w-[500px] flex flex-wrap items-center justify-center gap-4">
            <DownloadCTA
              hasSubscription={hasSubscription}
              variant="primary"
              className="flex-1 min-w-[190px] whitespace-nowrap"
            />

            <MenuGroup
              name="LearnMore"
              renderAs="hlist"
              itemsAsLinks={false}
              styleOverrides={{
                menu: "flex flex-row items-center py-0 my-0",
                menu_item: "list-none px-0 py-0 my-0",
              }}
              menuItemRender={(item) => (
                <Button
                  variant="secondary"
                  onClick={() => navigate(item.link?.[0]?.url)}
                  className="flex-1 min-w-[190px] whitespace-nowrap"
                >
                  {item.label}
                </Button>
              )}
            />
          </div>

          <p className="text-sm text-text_secondary">{texts?.hero_footer_note}</p>

          <ReleaseNotes
            texts={texts}
            className="min-w-[190px]"
            renderDownload={() => (
              <DownloadCTA hasSubscription={hasSubscription} variant="primary_m" />
            )}
          />
        </div>
      </div>
    </section>
  )
}

function Hero() {
  const hasSubscription = useHasActiveSubscription()
  const texts = TextBundle({ id: "home_hero_section" })
  const heroFeatures = getHeroFeatures(texts)

  return (
    <section className="hidden lg:block relative overflow-hidden rounded-b-[70px] xl:rounded-b-[100px]">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-secondary/15 to-secondary/30 pointer-events-none" />

      <div className="max-w-[500px] flex flex-col items-start gap-4 pl-6 lg:pl-10 pr-4 pt-24 2xl:pt-32 pb-20 relative z-10 xl:ml-6 2xl:ml-20">
        {/* h1 defaults to 56px, which overflows this max-w-md column and wraps
            the first line — step it down and let it grow again on wider screens */}
        <Typography 
          variant="h1" 
          className="lg:text-[42px] xl:text-[48px] 2xl:text-[52px]"
        >
          {texts?.hero_title_part1}<br />
          <span className="text-primary">
            {texts?.hero_title_part2}
          </span>
        </Typography>

        <Typography variant="body1" className="mb-0">
          {texts?.hero_subtitle_line1} {texts?.hero_subtitle_line2}
        </Typography>

        <ul className="flex flex-col gap-3 2xl:gap-4 my-2">
          {heroFeatures.map((feature) => (
            <li key={feature.title} className="flex items-center gap-3">
              <span className="w-10 lg:w-12 xl:w-14 h-10 lg:h-12 xl:h-14 rounded-xl bg-gradient-to-br from-secondary/50 to-accent text-white flex items-center justify-center shrink-0">
                <feature.icon className="w-7 h-7" />
              </span>

              <div>
                <Typography variant="body1_medium" className="my-0">
                  {feature.title}
                </Typography>
                <Typography variant="txt_dim">
                  {feature.text}
                </Typography>
              </div>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-4">
          <DownloadCTA hasSubscription={hasSubscription} variant="primary_m" />

          <MenuGroup
            name="LearnMore"
            renderAs="hlist"
            itemsAsLinks={false}
            styleOverrides={{
              menu: "flex flex-row items-center py-0 my-0",
              menu_item: "list-none px-0 py-0 my-0",
            }}
            menuItemRender={(item) => (
              <Button
                variant="secondary_m"
                onClick={() => navigate(item.link?.[0]?.url)}
              >
                {item.label}
              </Button>
            )}
          />
        </div>

        <Typography variant="txt1_dim">
          {texts?.hero_footer_note}</Typography>

        <ReleaseNotes
          texts={texts}
          renderDownload={() => (
            <DownloadCTA hasSubscription={hasSubscription} variant="primary_m" />
          )}
        />
      </div>

      <div className="relative lg:absolute w-full lg:w-auto xl:max-w-[1100px] h-64 sm:h-80 lg:h-auto lg:top-32 lg:bottom-[140px] 2xl:bottom-[110px] lg:left-[36%] xl:left-[34%] lg:right-0 overflow-hidden mt-12 lg:mt-0">
        <Image
          id="home_hero"
          className="w-full h-full lg:absolute lg:inset-0 object-contain object-left"
        />
      </div>
    </section>
  )
}

export default function HeroSection() {
  return (
    <>
      <HeroMobile />
      <Hero />
    </>
  )
}
